/**
 * Moz Error Parser Module (Story 20.7, AC #5, AC #7)
 *
 * Two-tier error mapping (carry-forward from Reddit 19.10 + Amazon Ads 19.13
 * fragile-regex hardening + Ahrefs 20.1 + Semrush 20.4):
 *
 *  1. **Primary** — JSON-RPC `result.error` envelope match. Moz V3 returns
 *     errors as `{ id, jsonrpc, error: { code, status, data: { explanation,
 *     issue, key }, message } }` with HTTP 200 OK. The `error.status` field
 *     carries the HTTP-shaped status the call WOULD have produced; we use it
 *     as the primary mapping signal alongside `error.code`.
 *  2. **Secondary** — HTTP status code fallback for transport-level errors
 *     (4xx/5xx) that aren't structured-coded in the JSON-RPC envelope. This
 *     handles cases where Moz returns a non-200 HTTP status before reaching
 *     the JSON-RPC layer (rate-limiter, gateway timeouts, etc).
 *
 * Sanitization: error messages MUST flow through `sanitizeMozErrorMessage`
 * before reaching any log line or `AdapterError.message`. Moz sends the API
 * token as a custom `x-moz-token` HEADER (not URL query param), so the
 * literal token value is LESS likely to leak into provider error messages
 * than Semrush — but error message echoes from the upstream JSON-RPC
 * envelope CAN still contain the token (if Moz echoes back what we sent in
 * a malformed-request error). Round 4 Red Team Attack #1: defensive
 * redaction is non-negotiable.
 *
 * **Source:** moz.com/api/docs/guides/error-handling verified 2026-05-10.
 */

import type { AdapterErrorCode } from "../adapter";
import { sanitizeErrorMessage } from "../safe-fetch";

// ============================================================================
// Types
// ============================================================================

/**
 * Closed enumeration of Moz error types. The `unknown` arm exists so the
 * parser never throws on unexpected payload shapes — the caller can still
 * surface a sanitized provider message even when no structured code matched.
 */
export type MozErrorType =
  | "unauthorized" // 401 — invalid or revoked token
  | "forbidden" // 403 — plan downgrade / method not in customer's tier
  | "not_found" // 404 — JSON-RPC method not found OR endpoint not Moz JSON-RPC
  | "validation_error" // 400 — malformed JSON-RPC envelope or missing required params
  | "rate_limit_exceeded" // 429 — 5-min sliding window breach
  | "provider_unavailable" // 5xx — Moz regional outage
  | "unknown";

export interface MozApiError {
  readonly type: MozErrorType;
  /** The Moz-documented error code (string). */
  readonly code: string | null;
  readonly message: string;
  readonly status: number | null;
  readonly retryable: boolean;
  /** Optional structured details echoed by Moz (`error.data.{explanation,issue,key}`). */
  readonly explanation?: string;
  readonly issue?: string;
}

/**
 * The shape of `result.error` returned by Moz V3 JSON-RPC error envelopes.
 * Verified moz.com/api/docs/guides/error-handling 2026-05-10.
 */
export interface MozJsonRpcErrorEnvelope {
  readonly code?: string | number;
  readonly status?: number;
  readonly message?: string;
  readonly data?: {
    readonly explanation?: string;
    readonly issue?: string;
    readonly key?: string;
  };
}

// ============================================================================
// Status fallback map
// ============================================================================

/**
 * Map an HTTP status code (or `error.status` field from JSON-RPC envelope) to
 * our internal type. Used as the fallback path when no specific Moz code matched.
 */
const STATUS_TO_TYPE: Record<number, { type: MozErrorType; retryable: boolean }> = {
  400: { type: "validation_error", retryable: false },
  401: { type: "unauthorized", retryable: false },
  403: { type: "forbidden", retryable: false },
  404: { type: "not_found", retryable: false },
  429: { type: "rate_limit_exceeded", retryable: true },
  500: { type: "provider_unavailable", retryable: true },
  502: { type: "provider_unavailable", retryable: true },
  503: { type: "provider_unavailable", retryable: true },
  504: { type: "provider_unavailable", retryable: true },
};

const TYPE_TO_ADAPTER_CODE: Record<MozErrorType, AdapterErrorCode> = {
  unauthorized: "token_expired",
  forbidden: "permission_denied",
  not_found: "account_not_found",
  validation_error: "invalid_request",
  rate_limit_exceeded: "rate_limited",
  provider_unavailable: "provider_unavailable",
  unknown: "invalid_response",
};

function isRetryableStatus(status: number): boolean {
  return status >= 500 || status === 429;
}

// ============================================================================
// Sanitization (Round 4 Red Team Attack #1, AC #5, AC #7)
// ============================================================================

/**
 * Wraps the generic `sanitizeErrorMessage` helper from safe-fetch with two
 * additional defenses specific to Moz:
 *
 *  1. Replace every literal occurrence of the decrypted plaintext API token
 *     with `[redacted-moz-token]`.
 *  2. Replace every `x-moz-token: <value>` header pattern in error message
 *     echoes with `x-moz-token: [redacted-moz-token]` — defends against the
 *     case where the request headers leak into a fetch error message
 *     ("got 401 with x-moz-token: abc123def header") BEFORE the token itself
 *     was supplied as a `decryptedToken` argument.
 *
 * Real Moz V3 tokens are typically JWT-shaped 200-400 chars per
 * moz.com/api/docs/guides/authentication verified 2026-05-10. Short test stubs
 * (single letters) would otherwise match every occurrence of the letter inside
 * human-readable text and corrupt the message — require a realistic minimum
 * token length before redacting on token-value match.
 */
export function sanitizeMozErrorMessage(
  message: string,
  decryptedToken?: string,
): string {
  // Run the generic sanitizer FIRST — it strips Bearer tokens, long base64
  // strings, generic OAuth-shape secrets.
  let sanitized = sanitizeErrorMessage(message);

  // (1) Replace `x-moz-token: <value>` header pattern wherever it appears in
  // the error string. Bounded `[^\s,;"'<>]{1,4096}` so adversarial inputs
  // cannot trigger backtracking; the token value runs until whitespace,
  // comma, semicolon, or quote (the realistic header-value terminators).
  // Multiline-aware so headers on separate lines all get caught.
  sanitized = sanitized.replace(
    /x-moz-token:\s*[^\s,;"'<>]{1,4096}/gi,
    "x-moz-token: [redacted-moz-token]",
  );

  // URL-encoded variant (defensive — Moz uses headers, not query params, but
  // a future debug endpoint or proxy might URL-encode): `x-moz-token%3A...`
  sanitized = sanitized.replace(
    /x-moz-token%3A[^\s,;"'<>]{1,4096}/gi,
    "x-moz-token%3A[redacted-moz-token]",
  );

  // (2) Exact-token replacement — defends against the case where the
  // decrypted plaintext token leaked into a non-header position (e.g. the
  // provider echoed the token value back in a malformed-request error body).
  //
  // **Round 1 review fix:** raised the threshold from 16 → 40 chars to match
  // the Zod connect schema's `apiToken.min(40)` lower bound. Real Moz V3
  // tokens are typically JWT-shaped 200-400 chars per moz.com/api/docs/guides/
  // authentication verified 2026-05-10; a 16-char threshold could match
  // unrelated short substrings of legitimate prose during ad-hoc dev-mode
  // testing. The 40-char floor mirrors the production input contract.
  if (decryptedToken && decryptedToken.length >= 40) {
    const escaped = decryptedToken.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    sanitized = sanitized.replace(new RegExp(escaped, "g"), "[redacted-moz-token]");
  }
  return sanitized;
}

// ============================================================================
// JSON-RPC envelope error parser (AC #7)
// ============================================================================

/**
 * Parse a JSON-RPC error envelope from a Moz V3 response.
 *
 * @param errorEnvelope - The `result.error` field from a JSON-RPC response.
 * @param httpStatus - HTTP status code from the underlying fetch (typically 200
 *   for envelope-encoded errors, but kept for forensic completeness).
 * @param decryptedToken - Optional plaintext token for sanitization.
 */
export function parseMozJsonRpcError(
  errorEnvelope: MozJsonRpcErrorEnvelope,
  httpStatus: number,
  decryptedToken?: string,
): MozApiError {
  const status =
    typeof errorEnvelope.status === "number" ? errorEnvelope.status : httpStatus;
  const code =
    errorEnvelope.code !== undefined && errorEnvelope.code !== null
      ? String(errorEnvelope.code)
      : null;
  const rawMessage = errorEnvelope.message ?? `Moz V3 JSON-RPC error (status ${status})`;
  const explanation = errorEnvelope.data?.explanation;
  const issue = errorEnvelope.data?.issue;

  // Two-tier mapping: status-based primary (Moz documents `error.status` as
  // the canonical per-error classifier per moz.com/api/docs/guides/error-handling
  // 2026-05-10), code-as-context secondary.
  const mapping = STATUS_TO_TYPE[status];
  const sanitizedMessage = sanitizeMozErrorMessage(
    `Moz error${code ? ` ${code}` : ""}: ${rawMessage}${
      explanation ? ` — ${explanation}` : ""
    }${issue ? ` [${issue}]` : ""}`,
    decryptedToken,
  );

  if (mapping) {
    return {
      type: mapping.type,
      code,
      message: sanitizedMessage,
      status,
      retryable: mapping.retryable,
      explanation,
      issue,
    };
  }

  // Status not in our table — defensive default.
  return {
    type: "unknown",
    code,
    message: sanitizedMessage,
    status,
    retryable: isRetryableStatus(status),
    explanation,
    issue,
  };
}

// ============================================================================
// HTTP-status-only fallback parser (AC #7 secondary tier)
// ============================================================================

/**
 * Parse a non-2xx HTTP response that is NOT a JSON-RPC error envelope.
 *
 * @param httpStatus - HTTP status code from the response.
 * @param body - Optional raw response body for forensic message context.
 * @param decryptedToken - Optional plaintext token for sanitization.
 * @returns A structured MozApiError, or null if the response was 2xx (caller
 *   should treat it as a possible JSON-RPC envelope).
 */
export function parseMozHttpError(
  httpStatus: number,
  body?: string,
  decryptedToken?: string,
): MozApiError | null {
  if (httpStatus >= 200 && httpStatus < 300) {
    return null; // Caller parses body as JSON-RPC envelope.
  }

  const sanitized = sanitizeMozErrorMessage(
    `Moz API error: HTTP ${httpStatus}${body ? ` — ${body.slice(0, 500)}` : ""}`,
    decryptedToken,
  );
  const mapping = STATUS_TO_TYPE[httpStatus];
  if (mapping) {
    return {
      type: mapping.type,
      code: null,
      message: sanitized,
      status: httpStatus,
      retryable: mapping.retryable,
    };
  }
  return {
    type: "unknown",
    code: null,
    message: sanitized,
    status: httpStatus,
    retryable: isRetryableStatus(httpStatus),
  };
}

/**
 * Map a `MozErrorType` to the canonical `AdapterErrorCode` used by the
 * adapter contract.
 */
export function mozErrorTypeToAdapterCode(type: MozErrorType): AdapterErrorCode {
  return TYPE_TO_ADAPTER_CODE[type];
}

// ============================================================================
// Throwable wrapper
// ============================================================================

/**
 * Throwable error class used by helpers to surface a parsed Moz error to the
 * adapter. Mirrors `SemrushApiCallError` shape so the adapter dispatch logic
 * stays uniform.
 */
export class MozApiCallError extends Error {
  readonly status: number;
  readonly type: MozErrorType;
  readonly code: string | null;
  readonly retryable: boolean;
  readonly explanation?: string;
  readonly issue?: string;

  constructor(parsed: MozApiError) {
    super(parsed.message);
    this.name = "MozApiCallError";
    this.status = parsed.status ?? 0;
    this.type = parsed.type;
    this.code = parsed.code;
    this.retryable = parsed.retryable;
    this.explanation = parsed.explanation;
    this.issue = parsed.issue;
  }
}

/**
 * Thrown by `pollMozAsyncCall` when a `result.async_call` chain does not
 * resolve within the 30-sec polling budget. Surfaces as `provider_unavailable`
 * retryable=true via parseMozApiError.
 */
export class MozAsyncTimeoutError extends Error {
  readonly asyncCallId: string;
  readonly elapsedMs: number;

  constructor(asyncCallId: string, elapsedMs: number) {
    super(
      `Moz async-call ${asyncCallId} did not resolve within ${elapsedMs}ms (30 sec budget). Retry on next sync tick.`,
    );
    this.name = "MozAsyncTimeoutError";
    this.asyncCallId = asyncCallId;
    this.elapsedMs = elapsedMs;
  }
}
